/**
 * Lot Map Selection Plugin for Native Interactive Site Map (ISM)
 *
 * Owns [data-panel] content when an Available lot with more than one buildable floor plan is
 * selected on the map — a plan picker for that lot, replacing the listing panel until the user
 * taps the map outside the selected lot (see lotInteraction.plugin.js's
 * onMapBackgroundClicked — there is no back/close control in this view's own header, by
 * product decision). Mirrors listView.plugin.js's render/remove lifecycle, gated on activeView
 * instead of the active chip (see LOT_MAP_SELECTION_PLUGIN_SEQUENCE.md).
 *
 * Also registers lotSelectedEpic here, since this plugin is the sole consumer of the
 * navigateTo(VIEW_LOT_MAP_SELECTION) transitions that epic produces.
 *
 * @module nativeInteractiveSiteMap/plugins/lotMapSelection.plugin
 */

import { distinctUntilChanged, filter, map, pairwise, tap } from 'rxjs/operators';
import createLotMapSelectionHeader from '../components/lotMapSelectionHeader.dom';
import createListingContent from '../components/listingContent.dom';
import { renderFloorPlanGrid } from '../components/floorPlanCards.dom';
import { renderQuickMoveInGrid } from '../components/quickMoveInCards.dom';
import {
  selectActiveView,
  selectSelectedLotId,
  selectLotMapSelectionViewModel,
} from '../redux/selectors';
import createLotSelectedEpic from '../redux/lotSelectedEpic';
import registerEpicOnce from '../redux/registerEpicOnce';
import { VIEW_LOT_MAP_SELECTION } from '../constants';

function selectViewAndSelectedLot(storeState) {
  return {
    activeView: selectActiveView(storeState),
    lotId: selectSelectedLotId(storeState),
  };
}

function viewAndSelectedLotEqual(previous, current) {
  return previous.activeView === current.activeView && previous.lotId === current.lotId;
}

export default function createLotMapSelectionPlugin() {
  const state = {
    header: null,
    content: null,
    panel: null,
    store$: null,
    getState: null,
    dispatch: null,
    dataActions: null,
    subscriptions: [],
  };

  function renderContent(lotId) {
    const viewModel = selectLotMapSelectionViewModel(state.getState(), lotId);

    state.header.setContent(viewModel);

    if (viewModel.housingUnit) {
      state.content.render(renderQuickMoveInGrid([viewModel.housingUnit]));
    } else if (viewModel.plans.length === 0) {
      state.content.clear();
    } else {
      state.content.render(renderFloorPlanGrid(viewModel.plans));
    }
  }

  function mount(lotId) {
    state.header = createLotMapSelectionHeader();
    state.content = createListingContent();

    state.panel.appendChild(state.header.getElement());
    state.panel.appendChild(state.content.getElement());

    renderContent(lotId);
  }

  function remove() {
    state.header?.remove();
    state.content?.remove();
    state.header = null;
    state.content = null;
  }

  // Tears down when the pair shows we just left lotMapSelection. A tap, not part of the render
  // decision below — leaving is the one transition that has nothing left to decide afterward.
  function removeIfLeavingView([previous, current]) {
    const wasInView = previous.activeView === VIEW_LOT_MAP_SELECTION;
    const isInView  = current.activeView === VIEW_LOT_MAP_SELECTION;
    if (wasInView && !isInView) {
      remove();
    }
  }

  function isCurrentlyInView([, current]) {
    return current.activeView === VIEW_LOT_MAP_SELECTION;
  }

  // Only ever called with pairs whose `current` is already lotMapSelection (see
  // isCurrentlyInView above) — deciding here is just first-entry vs. still-in-view.
  function renderForCurrentLot([previous, current]) {
    if (previous.activeView === VIEW_LOT_MAP_SELECTION) {
      // Still in this view — the selected lot changed (the user tapped a different qualifying
      // lot). Refresh in place instead of tearing down and recreating the header/content.
      renderContent(current.lotId);
    } else {
      mount(current.lotId);
    }
  }

  // pairwise() never emits on the very first store$ value after start() (it needs two values to
  // form a pair). Safe here only because viewReset (dispatched from index.js's onStop on modal
  // teardown) guarantees activeView is back to 'listing' every time this plugin is freshly
  // mounted — the dataSlice outlives the mobile modal's open/close cycle, so without that reset
  // a reopen could land on a first value that's already 'lotMapSelection', which pairwise()
  // would silently miss.
  function setupSubscriptions() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectViewAndSelectedLot),
        distinctUntilChanged(viewAndSelectedLotEqual),
        pairwise(),
        tap(removeIfLeavingView),
        filter(isCurrentlyInView),
      ).subscribe(renderForCurrentLot),
    );
  }

  return {
    name: 'lot-map-selection',

    async init(context) {
      const lotSelectedEpic = createLotSelectedEpic({
        dataActions: context.services.dataActions,
        isMobile: context.state.isMobile,
      });
      registerEpicOnce(context.services.epic$, 'lot-selected', lotSelectedEpic);
    },

    async start(context) {
      state.panel = context.root.querySelector('[data-panel]');
      if (!state.panel) {
        return;
      }

      const { getState, dispatch, dataActions, store$ } = context.services;
      state.getState = getState;
      state.dispatch = dispatch;
      state.dataActions = dataActions;
      state.store$ = store$;

      setupSubscriptions();
    },

    async stop() {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];
      remove();
      state.panel = null;
      state.store$ = null;
      state.getState = null;
      state.dispatch = null;
      state.dataActions = null;
    },
  };
}
